import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import { CalendarIcon, Download, FileText, IndianRupee, TrendingUp, ShoppingCart, Package } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Ledger() {
  const [bills, setBills] = useState([]);
  const [dailyReport, setDailyReport] = useState([]);
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async function() {
    setLoading(true);
    try {
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      
      const billsRes = await axios.get(`${API}/bills?start_date=${startStr}&end_date=${endStr}`);
      const reportRes = await axios.get(`${API}/reports/daily?start_date=${startStr}&end_date=${endStr}`);
      
      setBills(billsRes.data);
      setDailyReport(reportRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(function() {
    fetchData();
  }, [fetchData]);

  async function handleExport() {
    try {
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      
      const response = await axios.get(`${API}/reports/export?start_date=${startStr}&end_date=${endStr}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sales_${startStr}_to_${endStr}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported');
    } catch (error) {
      toast.error('Export failed');
    }
  }

  function calculateTotalSales() {
    let total = 0;
    for (let i = 0; i < dailyReport.length; i++) {
      total += dailyReport[i].total_sales;
    }
    return total;
  }

  function calculateTotalTransactions() {
    let total = 0;
    for (let i = 0; i < dailyReport.length; i++) {
      total += dailyReport[i].transaction_count;
    }
    return total;
  }

  function calculateTotalItems() {
    let total = 0;
    for (let i = 0; i < dailyReport.length; i++) {
      total += dailyReport[i].items_sold;
    }
    return total;
  }

  const totalSales = calculateTotalSales();
  const totalTransactions = calculateTotalTransactions();
  const totalItems = calculateTotalItems();
  const avgPerDay = dailyReport.length > 0 ? (totalSales / dailyReport.length) : 0;

  function getChartData() {
    const reversed = [];
    for (let i = dailyReport.length - 1; i >= 0; i--) {
      reversed.push(dailyReport[i]);
    }
    const sliced = reversed.slice(0, 14);
    const data = [];
    for (let i = 0; i < sliced.length; i++) {
      data.push({
        date: format(new Date(sliced[i].date), 'dd/MM'),
        sales: sliced[i].total_sales
      });
    }
    return data;
  }

  function renderDailyBreakdown() {
    if (dailyReport.length === 0) {
      return <p className="text-center text-slate-400 py-4">No data for selected period</p>;
    }
    const items = [];
    for (let i = 0; i < dailyReport.length; i++) {
      const day = dailyReport[i];
      items.push(
        <div key={day.date} className="flex justify-between items-center p-2 bg-slate-50 rounded">
          <div>
            <p className="font-medium text-sm">{format(new Date(day.date), 'dd MMM yyyy')}</p>
            <p className="text-xs text-slate-500">{day.transaction_count} bills</p>
          </div>
          <p className="font-mono font-medium text-mint-600">₹{day.total_sales.toFixed(2)}</p>
        </div>
      );
    }
    return items;
  }

  function renderBillRows() {
    if (bills.length === 0) {
      return (
        <tr>
          <td colSpan="6" className="text-center py-8 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No bills found for selected period</p>
          </td>
        </tr>
      );
    }
    const rows = [];
    for (let i = 0; i < bills.length; i++) {
      const bill = bills[i];
      rows.push(
        <tr key={bill.id} className="border-b hover:bg-slate-50" data-testid={`bill-row-${bill.id}`}>
          <td className="py-2 px-3 font-mono text-xs">{bill.bill_number}</td>
          <td className="py-2 px-3">{format(new Date(bill.created_at), 'dd/MM/yy HH:mm')}</td>
          <td className="py-2 px-3">
            <p>{bill.patient_name || 'Walk-in'}</p>
            {bill.doctor_name && (
              <p className="text-xs text-slate-500">Ref: {bill.doctor_name}</p>
            )}
          </td>
          <td className="py-2 px-3">{bill.items.length}</td>
          <td className="py-2 px-3">
            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs uppercase">
              {bill.payment_method}
            </span>
          </td>
          <td className="py-2 px-3 text-right font-mono font-medium">₹{bill.grand_total}</td>
        </tr>
      );
    }
    return rows;
  }

  const chartData = getChartData();

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Sales Ledger</h1>
          <p className="text-slate-500 text-sm">Digital ledger & daily sales reports</p>
        </div>
        <Button onClick={handleExport} className="export-btn" data-testid="export-csv-btn">
          <Download className="w-4 h-4" />
          Export CSV
        </Button>
      </div>

      {/* Date Filter */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>From:</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-40" data-testid="start-date-btn">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(startDate, 'dd/MM/yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={function(date) { if (date) setStartDate(date); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-2">
              <Label>To:</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-40" data-testid="end-date-btn">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(endDate, 'dd/MM/yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={function(date) { if (date) setEndDate(date); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button onClick={fetchData} disabled={loading}>
              {loading ? 'Loading...' : 'Apply'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-mint-100 rounded-lg flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-mint-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Sales</p>
                <p className="text-xl font-bold text-navy-900">₹{totalSales.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-navy-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Transactions</p>
                <p className="text-xl font-bold text-navy-900">{totalTransactions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Items Sold</p>
                <p className="text-xl font-bold text-navy-900">{totalItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg per Day</p>
                <p className="text-xl font-bold text-navy-900">₹{avgPerDay.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Chart */}
        <div className="col-span-12 lg:col-span-8">
          <Card>
            <CardContent className="p-4">
              <h3 className="font-heading font-semibold text-navy-900 mb-4">Sales Trend (Last 14 Days)</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                      formatter={function(value) { return [`₹${value.toFixed(2)}`, 'Sales']; }}
                    />
                    <Bar dataKey="sales" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Daily Breakdown */}
        <div className="col-span-12 lg:col-span-4">
          <Card>
            <CardContent className="p-4">
              <h3 className="font-heading font-semibold text-navy-900 mb-4">Daily Breakdown</h3>
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {renderDailyBreakdown()}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Recent Bills */}
        <div className="col-span-12">
          <Card>
            <CardContent className="p-4">
              <h3 className="font-heading font-semibold text-navy-900 mb-4">Recent Bills</h3>
              <ScrollArea className="h-80">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Bill No.</th>
                      <th className="text-left py-2 px-3">Date</th>
                      <th className="text-left py-2 px-3">Patient</th>
                      <th className="text-left py-2 px-3">Items</th>
                      <th className="text-left py-2 px-3">Payment</th>
                      <th className="text-right py-2 px-3">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderBillRows()}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Ledger;
